import { Button } from "@bcc/components/ui/button";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { useToast } from "@bcc/hooks/use-toast";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setConfigureProductModalTestId } from "./configure-product-modal.utils";

interface Product {
  id: string | number;
  name: string;
  sku: string;
  category: string;
  imageUrl: string;
  description: string;
  source: "manual" | "shopify" | "woocommerce";
  variants: Array<{
    id: string;
    size: string;
    sku: string;
    status: "published" | "out_of_stock" | "draft" | "rejected";
    subscribers: number;
    purchasePrice: number;
    salePrice: number;
    commission: number;
    payout: number;
    margin: number;
    weeklyLimit: number;
    termType: "long_term" | "short_term";
    termEndDate?: string;
    grindMappings: string[];
    stock: number;
  }>;
}

interface ConfigureProductModalProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  testId?: string;
}

export function ConfigureProductModal({ product, open, onOpenChange, onSave, testId }: ConfigureProductModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-populate with existing product data
  const [formData, setFormData] = useState({
    nameOfCoffee: product.name,
    productDescription: product.description,
    mainFlavorCategory: "",
    secondaryFlavor: "",
    tastingNotes: "",
    isDecaf: null as boolean | null,
    bagSize: product.variants[0]?.size || "",
    roasterLevel: "",
    coffeeType: "",
    country: "",
    process: "",
    varietal: "",
    elevationFrom: "",
    elevationTo: "",
    certifications: "",
    roasterNotes: "",
    roasterEffectiveDate: "",
    wouldYouRecommend: "",
    grind: "",
    region: "",
    additionalFlavorNotes: "",
    roasterComments: "",
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.mainFlavorCategory) {
      newErrors.mainFlavorCategory = t("views.configureProductModal.errors.mainFlavorCategoryRequired");
    }
    if (!formData.tastingNotes) {
      newErrors.tastingNotes = t("views.configureProductModal.errors.tastingNotesRequired");
    }
    if (formData.isDecaf === null) {
      newErrors.isDecaf = t("views.configureProductModal.errors.isDecafRequired");
    }
    if (!formData.bagSize) {
      newErrors.bagSize = t("views.configureProductModal.errors.bagSizeRequired");
    }
    if (!formData.roasterLevel) {
      newErrors.roasterLevel = t("views.configureProductModal.errors.roasterLevelRequired");
    }
    if (!formData.coffeeType) {
      newErrors.coffeeType = t("views.configureProductModal.errors.coffeeTypeRequired");
    }
    if (!formData.country) {
      newErrors.country = t("views.configureProductModal.errors.countryRequired");
    }
    if (!formData.roasterEffectiveDate) {
      newErrors.roasterEffectiveDate = t("views.configureProductModal.errors.roasterEffectiveDateRequired");
    }
    if (!formData.grind) {
      newErrors.grind = t("views.configureProductModal.errors.grindRequired");
    }
    if (!formData.region) {
      newErrors.region = t("views.configureProductModal.errors.regionRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: t("views.configureProductModal.toast.validationError"),
        description: t("views.configureProductModal.toast.fillRequiredFields"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: t("views.configureProductModal.toast.configSaved"),
        description: t("views.configureProductModal.toast.productUpdated", {
          name: formData.nameOfCoffee,
        }),
      });

      onSave();
      onOpenChange(false);
    } catch {
      toast({
        title: t("common.error"),
        description: t("views.configureProductModal.toast.saveFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent testId={testId ?? setConfigureProductModalTestId("content")} className="sm:max-w-[864px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("views.configureProductModal.title")}</DialogTitle>
          <DialogDescription>
            {t("views.configureProductModal.description", {
              name: product.name,
            })}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div
          data-testid={setConfigureProductModalTestId("progress-steps")}
          className="flex items-center justify-center gap-4 py-4 border-y"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-white">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <span data-testid={setConfigureProductModalTestId("step-product-details")} className="text-sm font-medium">
              {t("views.configureProductModal.steps.productDetails")}
            </span>
          </div>
          <div className="h-px w-12 bg-border" />
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">2</div>
            <span data-testid={setConfigureProductModalTestId("step-configure")} className="text-sm font-medium text-primary">
              {t("views.configureProductModal.steps.configure")}
            </span>
          </div>
          <div className="h-px w-12 bg-border" />
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-border text-muted-foreground">3</div>
            <span data-testid={setConfigureProductModalTestId("step-images")} className="text-sm text-muted-foreground">
              {t("views.configureProductModal.steps.images")}
            </span>
          </div>
          <div className="h-px w-12 bg-border" />
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-border text-muted-foreground">4</div>
            <span data-testid={setConfigureProductModalTestId("step-review")} className="text-sm text-muted-foreground">
              {t("views.configureProductModal.steps.review")}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="space-y-2">
            <Label data-testid={setConfigureProductModalTestId("label-main-flavor")} htmlFor="mainFlavorCategory">
              {t("views.configureProductModal.labels.mainFlavorCategory")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.mainFlavorCategory} onValueChange={(value) => setFormData({ ...formData, mainFlavorCategory: value })}>
              <SelectTrigger className={errors.mainFlavorCategory ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fruity">Fruity</SelectItem>
                <SelectItem value="nutty">Nutty</SelectItem>
                <SelectItem value="chocolate">Chocolate</SelectItem>
                <SelectItem value="floral">Floral</SelectItem>
                <SelectItem value="spicy">Spicy</SelectItem>
              </SelectContent>
            </Select>
            {errors.mainFlavorCategory && (
              <p
                data-testid={setConfigureProductModalTestId("error-main-flavor")}
                className="text-sm text-destructive flex items-center gap-1"
              >
                <AlertCircle className="h-4 w-4" />
                {errors.mainFlavorCategory}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label data-testid={setConfigureProductModalTestId("label-secondary-flavor")} htmlFor="secondaryFlavor">
              {t("views.configureProductModal.labels.secondaryFlavor")}
            </Label>
            <Select value={formData.secondaryFlavor} onValueChange={(value) => setFormData({ ...formData, secondaryFlavor: value })}>
              <SelectTrigger>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="berry">Berry</SelectItem>
                <SelectItem value="caramel">Caramel</SelectItem>
                <SelectItem value="citrus">Citrus</SelectItem>
                <SelectItem value="vanilla">Vanilla</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tastingNotes">
              {t("views.configureProductModal.labels.tastingNotes")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.tastingNotes} onValueChange={(value) => setFormData({ ...formData, tastingNotes: value })}>
              <SelectTrigger className={errors.tastingNotes ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bright">Bright</SelectItem>
                <SelectItem value="smooth">Smooth</SelectItem>
                <SelectItem value="bold">Bold</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
              </SelectContent>
            </Select>
            {errors.tastingNotes && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.tastingNotes}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isDecaf"
                checked={formData.isDecaf === true}
                onCheckedChange={(checked) => setFormData({ ...formData, isDecaf: checked as boolean })}
                className={errors.isDecaf ? "border-destructive" : ""}
              />
              <Label htmlFor="isDecaf" className="cursor-pointer">
                {t("views.configureProductModal.labels.isDecaf")} <span className="text-destructive">*</span>
              </Label>
            </div>
            {errors.isDecaf && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.isDecaf}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="roasterLevel">
              {t("views.configureProductModal.labels.roasterLevel")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.roasterLevel} onValueChange={(value) => setFormData({ ...formData, roasterLevel: value })}>
              <SelectTrigger className={errors.roasterLevel ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="medium-light">Medium-Light</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="medium-dark">Medium-Dark</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
              </SelectContent>
            </Select>
            {errors.roasterLevel && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.roasterLevel}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="coffeeType">
              {t("views.configureProductModal.labels.coffeeType")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.coffeeType} onValueChange={(value) => setFormData({ ...formData, coffeeType: value })}>
              <SelectTrigger className={errors.coffeeType ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="arabica">Arabica</SelectItem>
                <SelectItem value="robusta">Robusta</SelectItem>
                <SelectItem value="blend">Blend</SelectItem>
              </SelectContent>
            </Select>
            {errors.coffeeType && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.coffeeType}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">
              {t("views.configureProductModal.labels.country")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
              <SelectTrigger className={errors.country ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brazil">Brazil</SelectItem>
                <SelectItem value="colombia">Colombia</SelectItem>
                <SelectItem value="ethiopia">Ethiopia</SelectItem>
                <SelectItem value="kenya">Kenya</SelectItem>
                <SelectItem value="guatemala">Guatemala</SelectItem>
                <SelectItem value="costa-rica">Costa Rica</SelectItem>
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.country}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="grind">
              {t("views.configureProductModal.labels.grind")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.grind} onValueChange={(value) => setFormData({ ...formData, grind: value })}>
              <SelectTrigger className={errors.grind ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="whole-bean">Whole Bean</SelectItem>
                <SelectItem value="coarse">Coarse</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="fine">Fine</SelectItem>
                <SelectItem value="espresso">Espresso</SelectItem>
              </SelectContent>
            </Select>
            {errors.grind && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.grind}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="region">
              {t("views.configureProductModal.labels.region")} <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })}>
              <SelectTrigger className={errors.region ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="south-america">South America</SelectItem>
                <SelectItem value="central-america">Central America</SelectItem>
                <SelectItem value="africa">Africa</SelectItem>
                <SelectItem value="asia-pacific">Asia Pacific</SelectItem>
              </SelectContent>
            </Select>
            {errors.region && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.region}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="roasterEffectiveDate">
              {t("views.configureProductModal.labels.roasterEffectiveDate")} <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="roasterEffectiveDate"
                type="date"
                value={formData.roasterEffectiveDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    roasterEffectiveDate: e.target.value,
                  })
                }
                className={errors.roasterEffectiveDate ? "border-destructive" : ""}
              />
            </div>
            {errors.roasterEffectiveDate && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.roasterEffectiveDate}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between gap-3 pt-4 border-t">
            <Button
              data-testid={setConfigureProductModalTestId("cancel-button")}
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t("buttons.cancel")}
            </Button>
            <div className="flex gap-3">
              <Button data-testid={setConfigureProductModalTestId("save-draft-button")} type="button" variant="outline" disabled={loading}>
                {t("views.configureProductModal.saveAsDraft")}
              </Button>
              <Button data-testid={setConfigureProductModalTestId("submit-button")} type="submit" disabled={loading}>
                {loading ? t("views.configureProductModal.submitting") : t("views.configureProductModal.submitForApproval")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
